import React, { useState, useEffect, useRef } from 'react';
import { API, C, CHUNK_SIZE, UPLOAD_PARALLEL } from '../../constants';
import { formatSize } from '../../utils';

export default function UploadTab({ token, onDone }) {
  const [uploading, setUploading]           = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadType, setUploadType]         = useState('movie');
  const [selectedFile, setSelectedFile]     = useState(null);
  const [selectedThumb, setSelectedThumb]   = useState(null);
  const [thumbPreview, setThumbPreview]     = useState(null);
  const [customName, setCustomName]         = useState('');
  const [msg, setMsg]                       = useState('');
  const [uploadInfo, setUploadInfo]         = useState('');
  const [parsePreview, setParsePreview]     = useState(null);
  const cancelRef = useRef(false);
  const abortRef  = useRef(null);
  const fileRef   = useRef(null);
  const thumbRef  = useRef(null);

  const handleFileChange = e => {
    const f = e.target.files[0];
    if (!f) return;
    setSelectedFile(f);
    setCustomName(f.name.replace(/\.[^/.]+$/, ''));
    setParsePreview(null);
    if (uploadType === 'series') {
      fetch(`${API}/admin/series/parse`, { method:'POST', headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${token}` }, body: JSON.stringify({ filename: f.name }) })
        .then(r => r.json()).then(d => setParsePreview(d)).catch(() => {});
    }
  };

  const handleThumbChange = e => {
    const f = e.target.files[0];
    if (!f) return;
    setSelectedThumb(f);
    const r = new FileReader();
    r.onload = ev => setThumbPreview(ev.target.result);
    r.readAsDataURL(f);
  };

  useEffect(() => {
    if (uploadType === 'series' && selectedFile) {
      fetch(`${API}/admin/series/parse`, { method:'POST', headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${token}` }, body: JSON.stringify({ filename: selectedFile.name }) })
        .then(r => r.json()).then(d => setParsePreview(d)).catch(() => {});
    } else {
      setParsePreview(null);
    }
  }, [uploadType]);

  const cancelUpload = () => { cancelRef.current = true; abortRef.current?.abort(); setUploading(false); setUploadProgress(0); setUploadInfo(''); setMsg('Subida cancelada'); };
  const sleep = ms => new Promise(r => setTimeout(r, ms));

  const uploadChunkWithRetry = async (uploadId, chunkIndex, blob, maxRetries = 5) => {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      if (cancelRef.current) throw new Error('cancelled');
      let res;
      try {
        res = await fetch(`${API}/admin/upload-chunk`, {
          method: 'POST',
          headers: { Authorization:`Bearer ${token}`, 'X-Upload-ID': uploadId, 'X-Chunk-Index': String(chunkIndex), 'Content-Type': 'application/octet-stream' },
          body: blob,
          signal: abortRef.current?.signal
        });
      } catch (err) {
        if (cancelRef.current) throw new Error('cancelled');
        res = null;
      }
      if (res?.ok) return;
      // Errores 4xx (sesión vencida, chunk inválido…) no se arreglan reintentando.
      if (res && res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || `Chunk ${chunkIndex + 1} rechazado (HTTP ${res.status})`);
      }
      const wait = Math.min(2000 * Math.pow(2, attempt), 30000);
      setUploadInfo(`Reintentando chunk ${chunkIndex + 1} (intento ${attempt + 1}/${maxRetries})... esperando ${wait/1000}s`);
      await sleep(wait);
    }
    throw new Error(`Chunk ${chunkIndex + 1} falló después de ${maxRetries} intentos`);
  };

  const uploadFile = async () => {
    if (!selectedFile) { setMsg('Selecciona un archivo primero'); return; }
    if (!customName.trim()) { setMsg('Escribe un nombre'); return; }
    cancelRef.current = false;
    abortRef.current = new AbortController();
    setUploading(true); setUploadProgress(0); setMsg(''); setUploadInfo('');
    const ext = selectedFile.name.split('.').pop();
    const finalName = `${customName.trim()}.${ext}`;
    const fileSize = selectedFile.size;
    const totalChunks = Math.ceil(fileSize / CHUNK_SIZE);
    const chunkBytes = i => Math.min(CHUNK_SIZE, fileSize - i * CHUNK_SIZE);
    try {
      setUploadInfo('Iniciando subida...');
      const initRes = await fetch(`${API}/admin/upload-init`, {
        method: 'POST',
        headers: { Authorization:`Bearer ${token}`, 'X-Filename': finalName, 'X-Media-Type': uploadType, 'X-Total-Chunks': String(totalChunks), 'X-Total-Size': String(fileSize), 'X-Chunk-Size': String(CHUNK_SIZE) }
      });
      if (!initRes.ok) { const e = await initRes.json().catch(()=>({})); throw new Error(e.error || `Error iniciando upload (${initRes.status})`); }
      const { upload_id, resumed } = await initRes.json();
      // La carátula va después del init: si el nombre ya existía, el init falla
      // y antes ya se había sobrescrito la carátula del título existente.
      // Se sube en paralelo con el video para no retrasar los chunks.
      let thumbPromise = Promise.resolve(true);
      if (selectedThumb) {
        const tf = new FormData();
        tf.append('thumbnail', selectedThumb);
        tf.append('name', customName.trim());
        thumbPromise = fetch(`${API}/admin/upload-thumb`, { method:'POST', headers:{ Authorization:`Bearer ${token}` }, body:tf })
          .then(r => r.ok).catch(() => false);
      }
      const received = new Set();
      if (resumed) {
        const statusRes = await fetch(`${API}/admin/upload-status?upload_id=${upload_id}`, { headers:{ Authorization:`Bearer ${token}` } });
        if (statusRes.ok) { const status = await statusRes.json(); (status.received || []).forEach(i => received.add(i)); }
      }
      let doneBytes = 0;
      received.forEach(i => { doneBytes += chunkBytes(i); });
      const startBytes = doneBytes;
      const startTime = Date.now();
      const report = () => {
        setUploadProgress(Math.round((doneBytes / fileSize) * 100));
        const secs = (Date.now() - startTime) / 1000;
        const speed = secs > 0 ? (doneBytes - startBytes) / secs : 0;
        const mbps = (speed / 1024 / 1024).toFixed(1);
        const eta = speed > 0 ? Math.round((fileSize - doneBytes) / speed) : 0;
        const etaTxt = eta >= 3600 ? `${Math.floor(eta/3600)}h ${Math.floor(eta%3600/60)}m` : eta >= 60 ? `${Math.floor(eta/60)}m ${eta%60}s` : `${eta}s`;
        setUploadInfo(`${(doneBytes/1024/1024).toFixed(0)}/${(fileSize/1024/1024).toFixed(0)} MB · ${mbps} MB/s${speed > 0 ? ` · faltan ~${etaTxt}` : ''}`);
      };
      setUploadProgress(Math.round((doneBytes / fileSize) * 100));
      if (resumed && received.size > 0) setUploadInfo(`Reanudando: ${received.size}/${totalChunks} chunks ya estaban en el servidor`);
      // Varios chunks en vuelo a la vez: con uno solo la latencia de cada petición
      // deja la conexión ociosa y no se aprovecha el ancho de banda.
      const pending = [];
      for (let i = 0; i < totalChunks; i++) if (!received.has(i)) pending.push(i);
      let next = 0;
      const worker = async () => {
        while (next < pending.length) {
          if (cancelRef.current) throw new Error('cancelled');
          const i = pending[next++];
          const start = i * CHUNK_SIZE;
          await uploadChunkWithRetry(upload_id, i, selectedFile.slice(start, start + chunkBytes(i)));
          doneBytes += chunkBytes(i);
          report();
        }
      };
      try {
        await Promise.all(Array.from({ length: Math.min(UPLOAD_PARALLEL, pending.length) }, worker));
      } catch (err) {
        // Si un chunk falla definitivamente se detienen los demás workers.
        cancelRef.current = true;
        abortRef.current?.abort();
        throw err;
      }
      setUploadInfo('Finalizando en el servidor...');
      const completeRes = await fetch(`${API}/admin/upload-complete`, {
        method: 'POST', headers: { Authorization:`Bearer ${token}`, 'X-Upload-ID': upload_id }
      });
      if (!completeRes.ok) { const err = await completeRes.json().catch(() => ({})); throw new Error(err.error || 'Error finalizando la subida'); }
      const thumbFailed = !(await thumbPromise);
      setMsg(`✓ "${finalName}" subido correctamente${thumbFailed ? ' (la carátula falló; se generará una automática)' : ''}`);
      setSelectedFile(null); setSelectedThumb(null); setThumbPreview(null); setCustomName('');
      if (fileRef.current) fileRef.current.value = '';
      if (thumbRef.current) thumbRef.current.value = '';
      onDone && onDone();
    } catch (err) {
      if (err.message !== 'cancelled' && !(cancelRef.current && err.name === 'AbortError')) setMsg(`Error: ${err.message}`);
    } finally {
      setUploading(false); setUploadInfo('');
    }
  };

  const inp = { width:'100%', padding:'12px 16px', borderRadius:'8px', background:'rgba(255,255,255,0.04)', border:`1px solid ${C.accentBorder}`, color:C.text, fontSize:'14px', outline:'none', boxSizing:'border-box', fontFamily:'Inter, sans-serif', marginBottom:'12px' };

  return (
    <div>
      <h3 style={{ color:C.text, fontSize:'15px', fontWeight:'600', marginBottom:'20px' }}>Subir contenido</h3>
      <div style={{ marginBottom:'20px' }}>
        <p style={{ color:C.textMuted, fontSize:'13px', marginBottom:'10px' }}>Tipo:</p>
        <div style={{ display:'flex', gap:'10px' }}>
          {['movie','series'].map(t => (
            <button key={t} onClick={() => setUploadType(t)} style={{ padding:'8px 20px', borderRadius:'8px', border:`1px solid ${uploadType===t?'transparent':C.accentBorder}`, background:uploadType===t?`linear-gradient(135deg,${C.accent},${C.accentSoft})`:C.accentDim, color:uploadType===t?'#fff':C.textMuted, cursor:'pointer', fontSize:'14px' }}>
              {t==='movie'?'🎬 Película':'📺 Serie'}
            </button>
          ))}
        </div>
      </div>
      <div style={{ marginBottom:'20px' }}>
        <p style={{ color:C.textMuted, fontSize:'13px', marginBottom:'8px' }}>Nombre del título:</p>
        <input placeholder='Ej: Ratatouille (2007)' value={customName} onChange={e=>setCustomName(e.target.value)} style={{ ...inp, marginBottom:0 }} />
        <p style={{ color:C.textDim, fontSize:'11px', marginTop:'6px' }}>El video y la carátula usarán este mismo nombre</p>
      </div>
      <div style={{ marginBottom:'16px' }}>
        <p style={{ color:C.textMuted, fontSize:'13px', marginBottom:'8px' }}>Video:</p>
        <div style={{ background:C.bgCard, border:`2px dashed ${C.accentBorder}`, borderRadius:'12px', padding:'24px 16px', textAlign:'center' }}>
          <input ref={fileRef} type="file" accept="video/*" onChange={handleFileChange} style={{ display:'none' }} />
          <button onClick={() => fileRef.current?.click()} style={{ padding:'10px 24px', background:C.accentDim, border:`1px solid ${C.accentBorder}`, color:C.accentSoft, borderRadius:'8px', cursor:'pointer', fontSize:'14px', fontWeight:'500', marginBottom:'10px' }}>🎬 Elegir video</button>
          {selectedFile ? (
            <>
              <div style={{ padding:'8px 14px', background:'rgba(201,113,74,0.1)', borderRadius:'8px', border:`1px solid ${C.accentBorder}`, marginTop:'8px' }}>
                <p style={{ color:C.accentSoft, fontSize:'13px', fontWeight:'500' }}>📄 {selectedFile.name}</p>
                <p style={{ color:C.textMuted, fontSize:'11px', marginTop:'3px' }}>{formatSize(selectedFile.size)} · {Math.ceil(selectedFile.size/CHUNK_SIZE)} chunks de {CHUNK_SIZE/1024/1024} MB</p>
              </div>
              {parsePreview && (
                <div style={{ marginTop:'8px', padding:'8px 14px', background: parsePreview.detected ? 'rgba(39,174,96,0.1)' : 'rgba(255,255,255,0.04)', borderRadius:'8px', border:`1px solid ${parsePreview.detected ? 'rgba(39,174,96,0.3)' : C.border}` }}>
                  {parsePreview.detected
                    ? <p style={{ color:'#27ae60', fontSize:'12px', fontWeight:'500' }}>📺 Detectado: <strong>{parsePreview.series_name}</strong> · T{parsePreview.season}E{String(parsePreview.episode).padStart(2,'0')}</p>
                    : <p style={{ color:C.textDim, fontSize:'12px' }}>⚠ No se detectó patrón SxxExx — se guardará como archivo suelto</p>
                  }
                </div>
              )}
            </>
          ) : <p style={{ color:C.textDim, fontSize:'12px' }}>MP4, MKV, AVI, MOV, WEBM · Sin límite de tamaño</p>}
        </div>
      </div>
      <div style={{ marginBottom:'20px' }}>
        <p style={{ color:C.textMuted, fontSize:'13px', marginBottom:'8px' }}>Carátula (opcional):</p>
        <div style={{ background:C.bgCard, border:`2px dashed ${C.accentBorder}`, borderRadius:'12px', padding:'20px 16px', textAlign:'center' }}>
          <input ref={thumbRef} type="file" accept="image/*" onChange={handleThumbChange} style={{ display:'none' }} />
          {thumbPreview
            ? <div style={{ marginBottom:'12px' }}><img src={thumbPreview} alt="preview" style={{ width:'120px', height:'70px', objectFit:'cover', borderRadius:'8px', border:`1px solid ${C.accentBorder}` }}/></div>
            : <p style={{ fontSize:'28px', marginBottom:'10px' }}>🖼</p>
          }
          <button onClick={() => thumbRef.current?.click()} style={{ padding:'8px 20px', background:C.accentDim, border:`1px solid ${C.accentBorder}`, color:C.accentSoft, borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:'500' }}>
            {selectedThumb ? '🖼 Cambiar' : '🖼 Elegir carátula'}
          </button>
          {selectedThumb && <p style={{ color:C.accentSoft, fontSize:'12px', marginTop:'8px' }}>✓ {selectedThumb.name}</p>}
          {!selectedThumb && <p style={{ color:C.textDim, fontSize:'11px', marginTop:'8px' }}>JPG, PNG, WEBP</p>}
        </div>
      </div>
      {uploading && (
        <div style={{ marginBottom:'16px', background:C.bgCard, borderRadius:'12px', padding:'16px', border:`1px solid ${C.accentBorder}` }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'8px' }}>
            <span style={{ color:C.text, fontSize:'13px', fontWeight:'500' }}>{customName}</span>
            <span style={{ color:C.accentSoft, fontSize:'14px', fontWeight:'700' }}>{uploadProgress}%</span>
          </div>
          <div style={{ height:'10px', background:'rgba(255,255,255,0.08)', borderRadius:'5px', marginBottom:'8px' }}>
            <div style={{ height:'100%', width:`${uploadProgress}%`, background:`linear-gradient(90deg,${C.accent},${C.accentSoft})`, borderRadius:'5px', transition:'width 0.3s' }} />
          </div>
          {uploadInfo && <p style={{ color:C.textMuted, fontSize:'11px', marginBottom:'8px' }}>{uploadInfo}</p>}
          <p style={{ color:C.textDim, fontSize:'11px', marginBottom:'10px' }}>Si pierdes conexión o cancelas, vuelve a elegir el mismo archivo con el mismo nombre y la subida continuará donde se quedó (48 h).</p>
          <button onClick={cancelUpload} style={{ padding:'6px 16px', background:C.dangerDim, border:`1px solid rgba(192,57,43,0.3)`, color:C.danger, borderRadius:'6px', cursor:'pointer', fontSize:'12px' }}>Cancelar</button>
        </div>
      )}
      {msg && <p style={{ color:msg.includes('✓')?C.success:'#e07a5f', fontSize:'14px', marginBottom:'16px' }}>{msg}</p>}
      <button onClick={uploadFile} disabled={uploading||!selectedFile} style={{ width:'100%', padding:'14px', background:(uploading||!selectedFile)?C.textDim:`linear-gradient(135deg,${C.accent},${C.accentSoft})`, border:'none', color:'#fff', borderRadius:'10px', cursor:(uploading||!selectedFile)?'not-allowed':'pointer', fontSize:'15px', fontWeight:'600', fontFamily:'Inter, sans-serif' }}>
        {uploading ? `Subiendo ${uploadProgress}%...` : '⬆ Subir al servidor'}
      </button>
    </div>
  );
}
